import { api } from './client';

export interface Profile {
  email: string;
  firstName: string | null;
  lastName: string | null;
  country: string | null;
  addressLine1: string | null;
  city: string | null;
  postalCode: string | null;
  phone: string | null;
  birthDate: string | null;
  emailVerified?: boolean;
  kycStatus?: string;
  kycEnabled?: boolean;
  deletionScheduledAt?: string | null;
  [key: string]: unknown;
}

export async function requestAccountDeletion(): Promise<Profile> {
  const { data } = await api.post<Profile>('/users/me/deletion');
  return data;
}

export async function cancelAccountDeletion(): Promise<Profile> {
  const { data } = await api.delete<Profile>('/users/me/deletion');
  return data;
}

export interface KycRequestResult {
  kycStatus: string;
  kycLastChangedAt: string | null;
}

// Submit a KYC review request, optionally attaching ID documents.
// Backend accepts up to 3 files (pdf/jpg/png, ≤5 MB each) under the `files` field.
export async function requestKyc(files: File[] = []): Promise<KycRequestResult> {
  const fd = new FormData();
  for (const f of files.slice(0, 3)) fd.append('files', f);
  const { data } = await api.post<KycRequestResult>('/users/me/kyc-request', fd);
  return data;
}

export interface ProfileUpdate {
  firstName?: string;
  lastName?: string;
  country?: string;
  addressLine1?: string;
  city?: string;
  postalCode?: string;
  phone?: string;
  birthDate?: string;
}

export async function updateProfile(patch: ProfileUpdate): Promise<Profile> {
  const { data } = await api.patch<Profile>('/users/me', patch);
  return data;
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await api.post('/users/me/password', { currentPassword, newPassword });
}

export interface Wallet {
  availableCents: number;
  heldCents: number;
  totalCents: number;
}

export async function fetchProfile(): Promise<Profile> {
  const { data } = await api.get<Profile>('/users/me');
  return data;
}

export async function fetchWallet(): Promise<Wallet> {
  const { data } = await api.get<Wallet>('/wallet');
  return data;
}

export interface FavoriteProduct {
  id: string;
  name: string;
  platform: string;
  genres: string[];
  ageRating: string | null;
  ourPriceCents: number;
  coverUrl: string | null;
  imageUrl: string | null;
  available: boolean;
  qty: number;
  totalQty: number;
  liveCheapestCents: number | null;
  availableOfferCount: number;
}
export interface FavoriteItem {
  productId: string;
  product: FavoriteProduct;
}
export interface FavoriteList {
  items: FavoriteItem[];
  total: number;
}

export async function listFavorites(params: { limit?: number; offset?: number } = {}): Promise<FavoriteList> {
  const { data } = await api.get<FavoriteList>('/favorites', { params });
  return data;
}

export async function addFavorite(productId: string): Promise<void> {
  await api.post('/favorites', { productId });
}

export async function removeFavorite(productId: string): Promise<void> {
  await api.delete(`/favorites/${productId}`);
}

export interface LedgerEntry {
  id: string;
  orderId: string | null;
  amountCents: number; // signed: negative = debit, positive = credit
  type: string; // PURCHASE | TOPUP | REFUND | HOLD | RELEASE | ...
  description: string | null;
  createdAt: string;
}
export interface LedgerList {
  items: LedgerEntry[];
  total: number;
}

export async function listLedger(params: { limit?: number; offset?: number } = {}): Promise<LedgerList> {
  const { data } = await api.get<LedgerList>('/wallet/ledger', { params });
  return data;
}

export interface WalletConfig {
  minDepositCents: number;
  maxDepositCents: number;
  currency: string;
}

export async function fetchWalletConfig(): Promise<WalletConfig> {
  const { data } = await api.get<WalletConfig>('/wallet/config');
  return data;
}

export interface TopupSession {
  paymentId: string;
  sessionId: string;
  redirectUrl: string;
  status: string;
  amountCents: number;
}

/** Start a hosted-checkout top-up; returns a provider redirect URL. */
export async function createTopupSession(amountCents: number): Promise<TopupSession> {
  const { data } = await api.post<TopupSession>('/wallet/topup-session', { amountCents });
  return data;
}

export interface TopupStatus {
  paymentId: string;
  amountCents: number;
  status: 'PENDING' | 'COMPLETED' | 'FAILED' | string;
}

/** Poll a top-up session's status (used by the provider return page). */
export async function getTopupSession(paymentId: string): Promise<TopupStatus> {
  const { data } = await api.get<TopupStatus>(`/wallet/topup-session/${paymentId}`);
  return data;
}
